export const RECT_CALLOUT_TOOL_ID = "rect-callout";

export const getRectCalloutTailEdge = ({ from, to }) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;

  if (Math.abs(dx) > Math.abs(dy)) {
    return dx >= 0 ? "right" : "left";
  }

  return dy >= 0 ? "bottom" : "top";
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const getDefaultTailTip = ({
  left,
  top,
  right,
  bottom,
  tailDepth,
  tailEdge,
}) => {
  const midX = left + (right - left) / 2;
  const midY = top + (bottom - top) / 2;

  if (tailEdge === "top") {
    return { x: midX, y: top - tailDepth };
  }
  if (tailEdge === "left") {
    return { x: left - tailDepth, y: midY };
  }
  if (tailEdge === "right") {
    return { x: right + tailDepth, y: midY };
  }

  return { x: midX, y: bottom + tailDepth };
};

const getTailIntersection = ({ center, tailTip, left, top, right, bottom }) => {
  const vx = tailTip.x - center.x;
  const vy = tailTip.y - center.y;

  if (Math.abs(vx) < 1e-6 && Math.abs(vy) < 1e-6) {
    return {
      side: "bottom",
      baseCenter: { x: center.x, y: bottom },
    };
  }

  const candidates = [];

  if (Math.abs(vx) > 1e-6) {
    const tLeft = (left - center.x) / vx;
    const yLeft = center.y + tLeft * vy;
    if (tLeft > 0 && yLeft >= top - 1e-6 && yLeft <= bottom + 1e-6) {
      candidates.push({ t: tLeft, side: "left", point: { x: left, y: yLeft } });
    }

    const tRight = (right - center.x) / vx;
    const yRight = center.y + tRight * vy;
    if (tRight > 0 && yRight >= top - 1e-6 && yRight <= bottom + 1e-6) {
      candidates.push({
        t: tRight,
        side: "right",
        point: { x: right, y: yRight },
      });
    }
  }

  if (Math.abs(vy) > 1e-6) {
    const tTop = (top - center.y) / vy;
    const xTop = center.x + tTop * vx;
    if (tTop > 0 && xTop >= left - 1e-6 && xTop <= right + 1e-6) {
      candidates.push({ t: tTop, side: "top", point: { x: xTop, y: top } });
    }

    const tBottom = (bottom - center.y) / vy;
    const xBottom = center.x + tBottom * vx;
    if (tBottom > 0 && xBottom >= left - 1e-6 && xBottom <= right + 1e-6) {
      candidates.push({
        t: tBottom,
        side: "bottom",
        point: { x: xBottom, y: bottom },
      });
    }
  }

  if (!candidates.length) {
    return {
      side: "bottom",
      baseCenter: { x: center.x, y: bottom },
    };
  }

  candidates.sort((a, b) => a.t - b.t);
  return {
    side: candidates[0].side,
    baseCenter: candidates[0].point,
  };
};

export const getRectCalloutGeometry = ({
  from,
  to,
  tailEdge = "bottom",
  tailTip = null,
}) => {
  const left = Math.min(from.x, to.x);
  const top = Math.min(from.y, to.y);
  const right = Math.max(from.x, to.x);
  const bottom = Math.max(from.y, to.y);
  const width = right - left;
  const height = bottom - top;
  const horizontalTail = tailEdge === "top" || tailEdge === "bottom";
  const tailWidth = horizontalTail
    ? Math.max(14, Math.min(width * 0.26, 34))
    : Math.max(14, Math.min(height * 0.26, 34));
  const tailDepth = horizontalTail
    ? Math.max(12, Math.min(height * 0.24, 24))
    : Math.max(12, Math.min(width * 0.24, 24));
  const midX = left + width / 2;
  const midY = top + height / 2;
  const hasValidTailTip =
    tailTip && Number.isFinite(tailTip.x) && Number.isFinite(tailTip.y);
  const rawTailTip = hasValidTailTip ? { x: tailTip.x, y: tailTip.y } : null;
  const tailTipOnOrInsideBody =
    !!rawTailTip &&
    rawTailTip.x >= left - 1e-6 &&
    rawTailTip.x <= right + 1e-6 &&
    rawTailTip.y >= top - 1e-6 &&
    rawTailTip.y <= bottom + 1e-6;
  const resolvedTailTip =
    rawTailTip && !tailTipOnOrInsideBody
      ? rawTailTip
      : getDefaultTailTip({ left, top, right, bottom, tailDepth, tailEdge });

  const bodyRect = {
    left,
    top,
    right,
    bottom,
  };

  const { side, baseCenter } = getTailIntersection({
    center: { x: midX, y: midY },
    tailTip: resolvedTailTip,
    left,
    top,
    right,
    bottom,
  });

  let pathPoints;
  let tailBaseCenter = baseCenter;
  let tailLeft = midX;
  let tailRight = midX;
  let tailTop = midY;
  let tailBottom = midY;

  if (side === "top" || side === "bottom") {
    const half = Math.max(6, Math.min((right - left) / 2 - 2, tailWidth / 2));
    const baseLeftX = clamp(baseCenter.x - half, left + 2, right - 2);
    const baseRightX = clamp(baseCenter.x + half, left + 2, right - 2);
    tailLeft = baseLeftX;
    tailRight = baseRightX;
    if (side === "top") {
      pathPoints = [
        { x: left, y: top },
        { x: baseLeftX, y: top },
        { x: resolvedTailTip.x, y: resolvedTailTip.y },
        { x: baseRightX, y: top },
        { x: right, y: top },
        { x: right, y: bottom },
        { x: left, y: bottom },
      ];
    } else {
      pathPoints = [
        { x: left, y: top },
        { x: right, y: top },
        { x: right, y: bottom },
        { x: baseRightX, y: bottom },
        { x: resolvedTailTip.x, y: resolvedTailTip.y },
        { x: baseLeftX, y: bottom },
        { x: left, y: bottom },
      ];
    }
  } else {
    const half = Math.max(6, Math.min((bottom - top) / 2 - 2, tailWidth / 2));
    const baseTopY = clamp(baseCenter.y - half, top + 2, bottom - 2);
    const baseBottomY = clamp(baseCenter.y + half, top + 2, bottom - 2);
    tailTop = baseTopY;
    tailBottom = baseBottomY;
    if (side === "left") {
      pathPoints = [
        { x: left, y: top },
        { x: right, y: top },
        { x: right, y: bottom },
        { x: left, y: bottom },
        { x: left, y: baseBottomY },
        { x: resolvedTailTip.x, y: resolvedTailTip.y },
        { x: left, y: baseTopY },
      ];
    } else {
      pathPoints = [
        { x: left, y: top },
        { x: right, y: top },
        { x: right, y: baseTopY },
        { x: resolvedTailTip.x, y: resolvedTailTip.y },
        { x: right, y: baseBottomY },
        { x: right, y: bottom },
        { x: left, y: bottom },
      ];
    }
  }

  return {
    left,
    top,
    right,
    bottom,
    width,
    height,
    bodyRect,
    midX,
    midY,
    tailLeft,
    tailRight,
    tailTop,
    tailBottom,
    tailDepth,
    tailWidth,
    tailEdge: side,
    tailTip: resolvedTailTip,
    tailBaseCenter,
    pathPoints,
  };
};

export const drawRectCallout = ({
  ctx,
  from,
  to,
  tailEdge,
  tailTip,
  fillEnabled,
  fillColor,
}) => {
  const { width, height, pathPoints } = getRectCalloutGeometry({
    from,
    to,
    tailEdge,
    tailTip,
  });

  if (width < 8 || height < 8) {
    return;
  }

  ctx.beginPath();
  ctx.moveTo(pathPoints[0].x, pathPoints[0].y);
  for (let i = 1; i < pathPoints.length; i += 1) {
    ctx.lineTo(pathPoints[i].x, pathPoints[i].y);
  }
  ctx.closePath();

  if (fillEnabled) {
    ctx.fillStyle = fillColor;
    ctx.fill();
  }

  ctx.stroke();
};

export const rectCalloutToolConfig = {
  id: RECT_CALLOUT_TOOL_ID,
  usesPreview: true,
  cursor: "crosshair",
  onStart: () => true,
  onMove: ({ ctx, lastPointRef, nextPoint, fillEnabled, fillColor }) => {
    drawRectCallout({
      ctx,
      from: lastPointRef.current,
      to: nextPoint,
      tailEdge: getRectCalloutTailEdge({
        from: lastPointRef.current,
        to: nextPoint,
      }),
      fillEnabled,
      fillColor,
    });
  },
  onEnd: ({ ctx, lastPointRef, endPoint, fillEnabled, fillColor }) => {
    drawRectCallout({
      ctx,
      from: lastPointRef.current,
      to: endPoint,
      tailEdge: getRectCalloutTailEdge({
        from: lastPointRef.current,
        to: endPoint,
      }),
      fillEnabled,
      fillColor,
    });
  },
};

const RectCalloutToolButton = ({ activeTool, onSelectTool }) => {
  return (
    <button
      type="button"
      className={`btn ${
        activeTool === RECT_CALLOUT_TOOL_ID ? "btn-dark" : "btn-outline-dark"
      }`}
      onClick={() => onSelectTool(RECT_CALLOUT_TOOL_ID)}
      title="Rectangular Callout"
      aria-label="Rectangular Callout"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M2.5 2.5H13.5V10.5H9.25L8 13L6.75 10.5H2.5V2.5Z"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
};

export default RectCalloutToolButton;
